import { useEffect, useState } from 'react';
import { shownVideoAspect } from '../../videoShape';
/** Each tile names its pane key here (VRSLayout.tsx). */
const PANE_ATTRIBUTE = 'data-vrs-pane';
/**
 * Whether two measurements give each pane the same size.
 *
 * @param {Object} left - One measurement.
 * @param {Object} right - The other.
 * @returns {boolean}
 */
function sameSlots(left, right) {
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length
        && keys.every(key => left[key].width === right[key]?.width && left[key].height === right[key]?.height);
}
/**
 * The size of each pane on the web call stage and the shape of the video in
 * it, for the fit rules (pictureFit.ts) and the card of a fitted picture
 * (videoShape.ts fitToSlot). Measured on the page: panes change size with
 * the window, the chat rail and the tile view, and a video changes shape
 * when the sender turns the camera (the video element's resize event).
 *
 * @param {RefObject<HTMLElement>} stage - The call stage.
 * @param {string} layoutKey - Changes whenever the panes are rearranged.
 * @returns {IPictureShapes}
 */
export default function usePictureShapes(stage, layoutKey) {
    const [aspects, setAspects] = useState({});
    const [slots, setSlots] = useState({});
    useEffect(() => {
        const root = stage.current;
        if (!root || typeof window === 'undefined') {
            return undefined;
        }
        const readSlots = () => {
            const next = {};
            root.querySelectorAll(`[${PANE_ATTRIBUTE}]`).forEach(pane => {
                const key = pane.getAttribute(PANE_ATTRIBUTE);
                const { height, width } = pane.getBoundingClientRect();
                if (key && width > 0 && height > 0) {
                    next[key] = {
                        height,
                        width
                    };
                }
            });
            setSlots(current => {
                if (sameSlots(current, next)) {
                    return current;
                }
                return next;
            });
        };
        const readVideo = (video) => {
            const key = video.closest?.(`[${PANE_ATTRIBUTE}]`)?.getAttribute(PANE_ATTRIBUTE);
            // The browser reports the video as it shows it, turned.
            const aspect = shownVideoAspect({
                height: video.videoHeight,
                landscape: false,
                local: false,
                platform: 'web',
                width: video.videoWidth
            });
            if (!key || aspect === undefined) {
                return;
            }
            setAspects(current => {
                if (current[key] === aspect) {
                    return current;
                }
                return {
                    ...current,
                    [key]: aspect
                };
            });
        };
        // Media events do not bubble; the stage gets them in the capture phase.
        const onVideoShape = (event) => {
            const target = event.target;
            if (target?.tagName === 'VIDEO') {
                readVideo(target);
            }
        };
        readSlots();
        root.querySelectorAll('video').forEach(readVideo);
        root.addEventListener('loadedmetadata', onVideoShape, true);
        root.addEventListener('resize', onVideoShape, true);
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(readSlots);
        root.querySelectorAll(`[${PANE_ATTRIBUTE}]`).forEach(pane => observer?.observe(pane));
        window.addEventListener('resize', readSlots);
        return () => {
            root.removeEventListener('loadedmetadata', onVideoShape, true);
            root.removeEventListener('resize', onVideoShape, true);
            observer?.disconnect();
            window.removeEventListener('resize', readSlots);
        };
    }, [layoutKey, stage]);
    return {
        aspects,
        slots
    };
}
